import { useState, useRef, useEffect } from 'react'
import Flag from './Flag'
import { severityOf, bySeverity } from './severity'
import { Avatar, CreatedLine, TripsLine, openUser } from './bits'
import { userFullName } from './users'
import ChatSection from './ChatSection'
import PassengerRow from './PassengerRow'

function PayoutId({ id }) {
  if (!id) return null
  return (
    <button
      onClick={e => {
        e.stopPropagation()
        if (navigator.clipboard) navigator.clipboard.writeText(id)
      }}
      title='Copiar ID del payout'
      className='self-start font-mono text-[11px] text-warm_gray dark:text-gray-400 bg-ink/[0.04] dark:bg-dk-raised hover:bg-ink/[0.08] dark:hover:bg-dk-hover rounded-md px-2 py-0.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-coral/30'
    >
      ID {id}
    </button>
  )
}

function riskOf(flags) {
  if (!flags || flags.length === 0) return { label: 'Sin señales', tone: 'clear' }
  if (flags.some(f => severityOf(f) === 'hard')) return { label: 'Riesgo alto', tone: 'high' }
  return { label: 'Revisar', tone: 'medium' }
}

const RISK_PILL = {
  high: 'bg-coral/[0.12] text-coral_deep ring-1 ring-coral/30 dark:bg-red-500/10 dark:text-red-400',
  medium: 'bg-amber/[0.18] text-amber_deep ring-1 ring-amber/40 dark:bg-amber-500/10 dark:text-amber-400',
  clear: 'bg-emerald/10 text-emerald_deep ring-1 ring-emerald/25 dark:bg-emerald-500/10 dark:text-emerald-400'
}

const prefersReducedMotion =
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

const SWIPE_THRESHOLD = 110

export default function ReviewCard({ card, submitting, onApprove, onReject, onOpenLobby, onSkip }) {
  const [rejecting, setRejecting] = useState(false)
  const [reason, setReason] = useState('')
  const [drag, setDrag] = useState(0)
  const [dragging, setDragging] = useState(false)
  const [flung, setFlung] = useState(null)
  const startX = useRef(null)
  const movedRef = useRef(false)
  const dragRef = useRef(0)

  useEffect(() => {
    if (!submitting) {
      setDrag(0)
      setDragging(false)
      setFlung(null)
      dragRef.current = 0
    }
  }, [submitting])

  const { payment, lobby, driver, passengers, chat, direct_chats: directChats, chat_users: chatUsers, flags } = card

  const enableDrag = !prefersReducedMotion && !submitting && !rejecting

  const onPointerDown = e => {
    if (!enableDrag) return
    startX.current = e.clientX
    movedRef.current = false
    dragRef.current = 0
  }
  const onPointerMove = e => {
    if (!enableDrag || startX.current == null) return
    const dx = e.clientX - startX.current
    if (!movedRef.current && Math.abs(dx) > 8) {
      movedRef.current = true
      setDragging(true)
      try { e.currentTarget.setPointerCapture(e.pointerId) } catch (_) {}
    }
    if (movedRef.current) {
      dragRef.current = dx
      setDrag(dx)
    }
  }
  const endDrag = () => {
    if (startX.current == null) return
    const dx = dragRef.current
    startX.current = null
    setDragging(false)
    if (dx > SWIPE_THRESHOLD) {
      setFlung('right')
      onApprove(flags)
      return
    }
    setDrag(0)
    dragRef.current = 0
    if (dx < -SWIPE_THRESHOLD) setRejecting(true)
  }
  const onClickCapture = e => {
    if (movedRef.current) {
      e.preventDefault()
      e.stopPropagation()
      movedRef.current = false
    }
  }

  const journey = (lobby && lobby.Journey) || {}
  const lobbyId = (payment && payment.lobby_id) || (lobby && lobby._id)
  const driverName = userFullName(driver)
  const verified = driver && driver.user_metadata && driver.user_metadata.verified
  const allowedFlags = (driver && driver.user_metadata && driver.user_metadata.allowed_flags) || []
  const amount = Number(payment.amount || 0).toLocaleString('es-AR')
  const due = payment.payment_due ? new Date(payment.payment_due) : null
  const dueLabel = due && !isNaN(due.getTime()) ? due.toLocaleDateString('es-AR') : null
  const risk = riskOf(flags)
  const segColor =
    risk.tone === 'high' ? 'bg-coral' : risk.tone === 'medium' ? 'bg-amber' : 'bg-emerald'
  const segFilled = risk.tone === 'clear' ? 0 : Math.min(flags.length, 3)

  const dragStyle = {
    transform: flung === 'right'
      ? 'translateX(140%) rotate(8deg)'
      : dragging
      ? `translateX(${drag}px) rotate(${drag * 0.04}deg)`
      : undefined,
    transition: dragging ? 'none' : 'transform 0.3s cubic-bezier(0.22,0.61,0.36,1)',
    touchAction: 'pan-y'
  }

  return (
    <div
      data-testid='review-card'
      className='pr-card relative bg-white dark:bg-dk-surface rounded-[28px] ring-1 ring-warm_border dark:ring-dk-border w-full max-w-[30rem] mx-auto p-6 sm:p-7 flex flex-col gap-5 text-ink dark:text-gray-100'
      style={dragStyle}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onClickCapture={onClickCapture}
    >
      {dragging && drag > 40 && (
        <span
          className='absolute top-6 right-6 z-10 rotate-12 rounded-xl border-2 border-emerald px-3 py-1 font-display font-bold text-emerald pointer-events-none'
          style={{ opacity: Math.min((drag - 40) / 80, 1) }}
        >
          APROBAR
        </span>
      )}
      {dragging && drag < -40 && (
        <span
          className='absolute top-6 left-6 z-10 -rotate-12 rounded-xl border-2 border-coral px-3 py-1 font-display font-bold text-coral pointer-events-none'
          style={{ opacity: Math.min((-drag - 40) / 80, 1) }}
        >
          RECHAZAR
        </span>
      )}
      <div className='flex items-start justify-between gap-3'>
        <button
          className='flex items-center gap-3 text-left group min-w-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-coral/40 rounded-xl'
          onClick={() => driver && driver.user_id && openUser(driver.user_id)}
        >
          <Avatar src={driver && driver.picture} name={driverName} size={48} />
          <span className='min-w-0'>
            <span className='block font-display font-bold text-lg leading-tight group-hover:text-coral_deep truncate'>
              {driverName}
              {verified ? ' · verificado' : ''}
            </span>
            <span className='block text-sm text-warm_gray dark:text-gray-400 truncate'>
              {payment.bank_account && payment.bank_account.owner_full_name}
            </span>
            <CreatedLine user={driver} />
            <TripsLine user={driver} />
          </span>
        </button>
        <span
          className={`shrink-0 rounded-full px-3 py-1 text-xs font-display font-bold tracking-wide ${RISK_PILL[risk.tone]}`}
        >
          {risk.label}
        </span>
      </div>

      <div className='flex items-end justify-between gap-3'>
        <div className='min-w-0'>
          <p className='font-display font-bold text-5xl leading-none tabular-nums text-ink dark:text-gray-100'>
            {amount}
          </p>
          <p className='text-warm_gray dark:text-gray-400 text-sm mt-2'>
            <span className='font-semibold text-ink dark:text-gray-100'>{payment.currency}</span>
            {dueLabel ? ` · vence ${dueLabel}` : ''}
          </p>
        </div>
        <div className='flex gap-1 pb-1.5 shrink-0'>
          {[0, 1, 2].map(i => (
            <span
              key={i}
              className={`w-2 h-7 rounded-full ${i < segFilled ? segColor : 'bg-ink/10 dark:bg-dk-raised'}`}
            />
          ))}
        </div>
      </div>

      <button
        onClick={() => onOpenLobby && onOpenLobby(lobbyId)}
        className='flex items-center gap-2 self-start rounded-full bg-coral/[0.08] hover:bg-coral/15 px-3.5 py-1.5 text-sm font-medium text-coral_deep transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-coral/40'
      >
        <span className='text-coral' aria-hidden='true'>
          ◍
        </span>
        <span>
          {journey.origin_name && journey.origin_name.locality} →{' '}
          {journey.destination_name && journey.destination_name.locality}
        </span>
      </button>

      <PayoutId id={payment._id} />

      <div>
        <p className='font-display font-bold text-sm mb-2'>
          Señales <span className='text-warm_gray dark:text-gray-400 font-body font-medium'>· {flags.length}</span>
        </p>
        {flags.length === 0 ? (
          <p className='text-sm text-warm_gray dark:text-gray-400'>Sin señales de riesgo</p>
        ) : (
          <div className='flex flex-col gap-2'>
            {bySeverity(flags).map((f, i) => (
              <Flag key={i} flag={f} allowed={severityOf(f) !== 'hard' && allowedFlags.includes(f.code)} />
            ))}
          </div>
        )}
      </div>

      <div>
        <p className='font-display font-bold text-sm mb-2'>Pasajeros</p>
        <div className='flex flex-col gap-1'>
          {passengers.map((p, i) => (
            <PassengerRow key={(p.companion && p.companion.user_id) || i} passenger={p} />
          ))}
        </div>
      </div>

      <ChatSection
        chat={chat}
        directChats={directChats}
        chatUsers={chatUsers}
        driverId={payment && payment.user_id}
        passengerIds={passengers.map(p => p.companion && p.companion.user_id).filter(Boolean)}
      />

      {rejecting ? (
        <div className='flex flex-col gap-3'>
          <textarea
            className='border border-warm_border dark:border-dk-border bg-white dark:bg-dk-raised rounded-2xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-coral/40 resize-none'
            rows={3}
            placeholder='Motivo del rechazo'
            value={reason}
            onChange={e => setReason(e.target.value)}
            autoFocus
          />
          <div className='flex gap-3'>
            <button
              className='flex-1 bg-coral hover:bg-coral_deep text-white font-display font-bold py-3 rounded-2xl disabled:opacity-40 transition-colors'
              disabled={!reason || submitting}
              onClick={() => onReject(reason, flags)}
            >
              Confirmar rechazo
            </button>
            <button
              className='px-5 py-3 rounded-2xl font-medium text-ink/70 dark:text-gray-300 hover:bg-ink/5 dark:hover:bg-dk-hover transition-colors disabled:opacity-40'
              disabled={submitting}
              onClick={() => setRejecting(false)}
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <div className='flex flex-col gap-2 pt-1'>
          <div className='flex gap-3'>
            <button
              onClick={() => onApprove(flags)}
              disabled={submitting}
              className='flex-1 flex items-center justify-center gap-2 bg-emerald hover:bg-emerald_deep text-white font-display font-bold text-lg py-3.5 rounded-2xl shadow-lg shadow-emerald/30 active:scale-[.98] transition-all focus:outline-none focus-visible:ring-4 focus-visible:ring-emerald/30 disabled:opacity-40 disabled:active:scale-100'
            >
              Aprobar <span aria-hidden='true'>→</span>
            </button>
            <button
              onClick={() => setRejecting(true)}
              disabled={submitting}
              className='flex-1 flex items-center justify-center gap-2 bg-coral hover:bg-coral_deep text-white font-display font-bold text-lg py-3.5 rounded-2xl shadow-lg shadow-coral/30 active:scale-[.98] transition-all focus:outline-none focus-visible:ring-4 focus-visible:ring-coral/25 disabled:opacity-40 disabled:active:scale-100'
            >
              <span aria-hidden='true'>←</span> Rechazar
            </button>
          </div>
          {onSkip && (
            <button
              onClick={onSkip}
              className='self-center rounded-lg px-3 py-1 text-sm font-medium text-warm_gray dark:text-gray-400 hover:text-ink dark:hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-coral/30'
            >
              Saltar por ahora ↑
            </button>
          )}
        </div>
      )}
    </div>
  )
}
